import { useState, useEffect, useRef } from 'react';
import { FlowNode } from '../../types';

interface Props {
  node: FlowNode;
  onUpdate: (updated: FlowNode) => void;
}

interface SwitchCase {
  id: string;
  value: string;
}

function toCases(raw: unknown): SwitchCase[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(c => {
    const obj = (c ?? {}) as Record<string, unknown>;
    return {
      id: typeof obj.id === 'string' ? obj.id : '',
      value: typeof obj.value === 'string' ? obj.value : '',
    };
  }).filter(c => c.id);
}

export function SwitchStepConfig({ node, onUpdate }: Props) {
  const config = (node.config ?? {}) as Record<string, unknown>;
  const idCounter = useRef(0);

  const [alias, setAlias] = useState((config.alias as string) ?? '');
  const [expression, setExpression] = useState((config.expression as string) ?? '');
  const [cases, setCases] = useState<SwitchCase[]>(toCases(config.cases));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setAlias((config.alias as string) ?? '');
    setExpression((config.expression as string) ?? '');
    setCases(toCases(config.cases));
  }, [node.id]);

  useEffect(() => {
    const { alias: _a, expression: _e, cases: _c, ...rest } = config;
    const serializedCases = cases
      .filter(c => c.value.trim())
      .map(c => ({ id: c.id, value: c.value.trim() }));
    const next = {
      ...rest,
      ...(expression.trim() ? { expression: expression.trim() } : {}),
      ...(serializedCases.length > 0 ? { cases: serializedCases } : {}),
      ...(alias.trim() ? { alias: alias.trim() } : {}),
    };
    if (JSON.stringify(next) === JSON.stringify(node.config ?? {})) return;
    const t = setTimeout(() => onUpdate({ ...node, config: next }), 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, expression, cases]);

  function addCase() {
    idCounter.current += 1;
    const newId = `case-${Date.now()}-${idCounter.current}`;
    setCases([...cases, { id: newId, value: '' }]);
  }

  function removeCase(id: string) {
    setCases(cases.filter(c => c.id !== id));
  }

  function updateCase(id: string, value: string) {
    setCases(cases.map(c => (c.id === id ? { ...c, value } : c)));
  }

  return (
    <div className="flex flex-col gap-3 flex-1 overflow-y-auto">

      {/* Alias */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Step alias</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="e.g. tool-switch"
          value={alias}
          onChange={e => setAlias(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
        />
        {alias && (
          <p className="text-xs text-muted-foreground">
            Matched value via <span className="font-mono text-tone-blue">{`{{${alias}.matched}}`}</span>
          </p>
        )}
      </div>

      {/* Switch expression */}
      <div className="flex flex-col gap-1">
        <label className="text-xs text-muted-foreground font-medium">Switch expression</label>
        <input
          type="text"
          className="border border-input rounded p-1.5 text-xs font-mono bg-background"
          placeholder="{{body.tool}}"
          value={expression}
          onChange={e => setExpression(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Resolved value is compared (case-insensitive) against each case value. First match wins.
        </p>
      </div>

      {/* Cases */}
      <div className="flex flex-col gap-2">
        <label className="text-xs text-muted-foreground font-medium">Cases</label>
        {cases.map(c => (
          <div key={c.id} className="flex gap-1 items-center">
            <input
              type="text"
              className="flex-1 border border-input rounded p-1.5 text-xs font-mono bg-background"
              placeholder="value, e.g. HelloHQ"
              value={c.value}
              onChange={e => updateCase(c.id, e.target.value)}
            />
            <button
              onClick={() => removeCase(c.id)}
              className="text-destructive hover:text-destructive/80 text-xs px-2"
              title="Remove case"
            >
              ✕
            </button>
          </div>
        ))}
        <button
          onClick={addCase}
          className="w-full border border-input text-xs text-muted-foreground rounded py-1 hover:bg-muted"
        >
          + Add case
        </button>
        <p className="text-xs text-muted-foreground">
          Each case adds an output handle on the node — wire it to the step that should run for that
          value (e.g. a <span className="font-mono">For Each</span>). If no case matches, the
          <span className="font-mono"> default</span> output is taken; if it is not wired, the flow ends without error.
        </p>
      </div>
    </div>
  );
}
